import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { AuthContext } from '../../context/AuthContext';
import { workOrderApi } from '../../api/workOrderApi';
import OrgTreeSelect from './WorkOrderOrgTreeSelect';
import { EditorContent } from '@tiptap/react';
import EditorToolbar from '../../components/editor/EditorToolbar';
import tiptapExtensions from '../../components/editor/tiptapExtensions';
import useEditorWithDragDrop from '../../components/editor/useEditorWithDragDrop';
import FileAttachment from '../../components/FileAttachment/FileAttachment';
import CommonDatePicker from '../../components/DatePicker/CommonDatePicker';
import UserSearchResultModal from './UserSearchResultModal';
import './WorkOrderFormModal.css';

// 중요도 옵션
const PRIORITY_OPTIONS = [
  { value: 'urgent', label: '긴급', icon: '🔴', desc: '최우선 처리가 요구되는 긴급 업무입니다.' },
  { value: 'high',   label: '높음', icon: '🟠', desc: '일반 업무보다 우선하여 처리해야 하는 업무입니다.' },
  { value: 'normal', label: '보통', icon: '🔵', desc: '일반적인 일정에 따라 수행하는 표준 업무입니다.' },
  { value: 'low',    label: '낮음', icon: '⚪', desc: '일정 여유가 있는 보조/참고성 업무입니다.' },
];

const getStatusMeta = (status) => {
  switch (status) {
    case 'progress':
      return { key: 'progress', label: '진행중' };
    case 'review':
      return { key: 'review', label: '결과검토' };
    case 'done':
      return { key: 'done', label: '완료' };
    case 'cancel':
      return { key: 'cancel', label: '취소' };
    case 'temp':
      return { key: 'temp', label: '임시저장' };
    case 'request':
    default:
      return { key: 'request', label: '대기(미접수)' };
  }
};

const getAssigneeStatusMeta = (status) => {
  switch (status) {
    case 'complete':
    case 'completed':
    case 'done':
      return { label: '완료', bg: '#10b981', color: '#ffffff', tooltip: '처리결과가 최종 확인완료되어 완료되었습니다.' };
    case 'review':
    case 'submitted':
      return { label: '검토', bg: '#8b5cf6', color: '#ffffff', tooltip: '처리결과가 제출되어 요청자 검토 대기 중입니다.' };
    case 'reject':
      return { label: '보완', bg: '#ea580c', color: '#ffffff', tooltip: '처리결과 보완요청 상태입니다.' };
    case 'progress':
      return { label: '진행', bg: '#2563eb', color: '#ffffff', tooltip: '업무를 접수하여 정상 진행 중입니다.' };
    case 'delay':
      return { label: '지연', bg: '#ef4444', color: '#ffffff', tooltip: '접수 기한을 초과하여 접수가 지연된 상태입니다.' };
    case 'pending':
    case 'request':
    default:
      return { label: '대기', bg: '#f59e0b', color: '#ffffff', tooltip: '업무를 아직 접수하지 않은 상태입니다 (미접수).' };
  }
};

function WorkOrderFormModal({
  open = true,
  mode = 'create',
  initialData,
  workOrderId,
  isSubModal = false,
  zIndex = null,
  onClose,
  onSaved,
  onStartEdit,
  onDelete,
  onDeleted
}) {
  const { user } = useContext(AuthContext);
  const isView = mode === 'view';
  const isEdit = mode === 'edit';

  // ── 폼 상태 ──
  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState('normal');
  const [completionType, setCompletionType] = useState('individual');
  const [assignees, setAssignees] = useState([]);
  const [ccs, setCcs] = useState([]);
  const [assigneeSearchTerm, setAssigneeSearchTerm] = useState('');
  const [ccSearchTerm, setCcSearchTerm] = useState('');

  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [dueDate, setDueDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 3);
    d.setHours(0, 0, 0, 0);
    return d;
  });

  const [editorHeight, setEditorHeight] = useState('400px');
  const [files, setFiles] = useState([]);
  const [showOrgPopup, setShowOrgPopup] = useState(false);
  const [orgTarget, setOrgTarget] = useState('');
  const [detailData, setDetailData] = useState(null);
  const [loading, setLoading] = useState(false);

  // 검색결과 다건 선택 모달 상태
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchModalUsers, setSearchModalUsers] = useState([]);
  const [searchModalTarget, setSearchModalTarget] = useState('assignees');

  // Tiptap 에디터
  const editor = useEditorWithDragDrop(tiptapExtensions);

  // ── [업무 관리 ▴] 드롭업 메뉴 상태 및 외부 클릭 닫기 ──
  const [showManagementMenu, setShowManagementMenu] = useState(false);
  const managementMenuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (managementMenuRef.current && !managementMenuRef.current.contains(e.target)) {
        setShowManagementMenu(false);
      }
    };
    if (showManagementMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showManagementMenu]);

  // 권한 판별
  const currentUserIdStr = String(user?.id || user?.userid || '');
  const authorIdStr = String(detailData?.author_id || detailData?.author?.id || detailData?.author?.userid || initialData?.author_id || '');
  const isAuthor = !authorIdStr || currentUserIdStr === authorIdStr;
  const isAdmin = user?.is_admin === true || user?.is_admin === 1 || user?.role === 'admin';
  const canManage = isAuthor || isAdmin;
  const myAssignee = (detailData?.assignees || initialData?.assignees || []).find(a =>
    String(a.user_id) === currentUserIdStr ||
    String(a.user?.id) === currentUserIdStr ||
    String(a.user?.userid) === currentUserIdStr ||
    String(a.target_id) === currentUserIdStr
  );
  const isAssignee = !!myAssignee;

  // ── 상세 데이터 로드 ──
  useEffect(() => {
    if ((mode === 'view' || mode === 'edit') && workOrderId && !initialData) {
      setLoading(true);
      workOrderApi.getDetail(workOrderId)
        .then(res => {
          const d = res.data || res;
          setDetailData(d);
          populateForm(d);
        })
        .catch(err => {
          console.error('상세 조회 실패:', err);
          alert('데이터를 불러오지 못했습니다.');
          onClose();
        })
        .finally(() => setLoading(false));
    } else if (initialData) {
      setDetailData(initialData);
      populateForm(initialData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, workOrderId, initialData]);

  const populateForm = useCallback((d) => {
    if (!d) return;
    setTitle(d.title || '');
    setPriority(d.priority || 'normal');
    setCompletionType(d.completion_type || 'individual');
    if (d.start_date) setStartDate(new Date(d.start_date));
    if (d.due_date) setDueDate(new Date(d.due_date));
    if (d.assignees) {
      setAssignees(d.assignees.map(a => {
        const u = a.user || {};
        const pos = u.org?.position || u.position || a.position || '';
        const dept = u.org?.dept_name || u.dept_name || a.department || '';
        const name = u.name || a.name || a.disp_target_name?.split(' ')[0] || '';
        const disp = name ? `${name}${pos ? ' / ' + pos : ''}${dept ? ' (' + dept + ')' : ''}` : (a.disp_target_name || String(u.userid || a.user_id));
        return {
          target_type: 'user',
          target_id: String(u.userid || a.target_id || a.emp_no || a.user_id),
          target_name: name,
          position: pos,
          dept_name: dept,
          disp_target_name: disp,
          status: a.status || 'request',
          progress_rate: a.progress_rate !== undefined && a.progress_rate !== null ? Number(a.progress_rate) : 0
        };
      }));
    }
    if (d.ccs) {
      setCcs(d.ccs.map(c => {
        const u = c.user || {};
        const pos = u.org?.position || u.position || c.position || '';
        const dept = u.org?.dept_name || u.dept_name || c.department || '';
        const name = u.name || c.name || c.disp_target_name?.split(' ')[0] || '';
        const disp = name ? `${name}${pos ? ' / ' + pos : ''}${dept ? ' (' + dept + ')' : ''}` : (c.disp_target_name || String(u.userid || c.user_id));
        return {
          target_type: 'user',
          target_id: String(u.userid || c.target_id || c.emp_no || c.user_id),
          target_name: name,
          position: pos,
          dept_name: dept,
          disp_target_name: disp
        };
      }));
    }
    if (d.files) {
      setFiles((d.files || []).map(f => ({
        ...f,
        id: f.id,
        name: f.file_name || f.originalName || f.name,
        originalName: f.file_name || f.originalName || f.name,
        size: f.file_size ?? f.size ?? 0,
        path: f.file_path || f.path || '',
        isNew: false,
        isDeleted: false
      })));
    }
    if (editor && d.content) {
      setTimeout(() => editor.commands.setContent(d.content), 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  // ── 선택된 사용자 목록 등록 처리 (상호 배타성 및 요청자 본인 배제) ──
  const applySelectedUsers = useCallback((itemsToAdd, target) => {
    if (!itemsToAdd || itemsToAdd.length === 0) return;

    const myId = String(user?.id || '');
    const myUserId = String(user?.userid || '');
    const isSelf = (item) => {
      const tId = String(item.target_id || item.userid || item.user_id || item.id || '');
      return Boolean((myId && tId === myId) || (myUserId && tId === myUserId));
    };

    if (target === 'assignees') {
      // 1. 요청자 본인 포함 차단
      const selfItem = itemsToAdd.find(isSelf);
      if (selfItem) {
        alert('요청자 본인은 담당자로 지정할 수 없습니다.');
        setAssigneeSearchTerm('');
        return;
      }

      // 2. 이미 참조자에 등록된 사용자 차단
      const ccIds = ccs.map(c => String(c.target_id));
      const alreadyInCc = itemsToAdd.find(n => ccIds.includes(String(n.target_id)));
      if (alreadyInCc) {
        alert(`이미 참조자로 등록된 사용자(${alreadyInCc.target_name || alreadyInCc.name})는 담당자로 등록할 수 없습니다.`);
        setAssigneeSearchTerm('');
        return;
      }

      // 3. 이미 담당자에 등록된 사용자 차단 (단 1회만 경고 후 종료)
      const existingIds = assignees.map(p => String(p.target_id));
      const toAdd = itemsToAdd.filter(n => !existingIds.includes(String(n.target_id)));
      if (toAdd.length === 0) {
        alert('이미 담당자로 등록된 사용자입니다.');
        setAssigneeSearchTerm('');
        return;
      }

      setAssignees(prev => [...prev, ...toAdd]);
      setAssigneeSearchTerm('');
    } else {
      // 1. 요청자 본인 포함 차단
      const selfItem = itemsToAdd.find(isSelf);
      if (selfItem) {
        alert('요청자 본인은 참조자로 지정할 수 없습니다.');
        setCcSearchTerm('');
        return;
      }

      // 2. 이미 담당자에 등록된 사용자 차단
      const assigneeIds = assignees.map(a => String(a.target_id));
      const alreadyInAssignee = itemsToAdd.find(n => assigneeIds.includes(String(n.target_id)));
      if (alreadyInAssignee) {
        alert(`이미 담당자로 등록된 사용자(${alreadyInAssignee.target_name || alreadyInAssignee.name})는 참조자로 등록할 수 없습니다.`);
        setCcSearchTerm('');
        return;
      }

      // 3. 이미 참조자에 등록된 사용자 차단 (단 1회만 경고 후 종료)
      const existingIds = ccs.map(p => String(p.target_id));
      const toAdd = itemsToAdd.filter(n => !existingIds.includes(String(n.target_id)));
      if (toAdd.length === 0) {
        alert('이미 참조자로 등록된 사용자입니다.');
        setCcSearchTerm('');
        return;
      }

      setCcs(prev => [...prev, ...toAdd]);
      setCcSearchTerm('');
    }
  }, [user, assignees, ccs]);

  // ── 인원 검색 (2글자 이상, 단건 자동 추가 / 다건 팝업 선택, 검색 즉시 알림) ──
  const handleUserSearch = async (term, target) => {
    const keyword = (term || '').trim();
    if (!keyword) { alert('검색할 이름을 입력해주세요.'); return; }
    if (keyword.length < 2) { alert('검색어를 2자리 이상 입력해 주세요.'); return; }

    const token = user?.token || sessionStorage.getItem('token');
    try {
      const res = await axios.get('/api/org/search', { params: { keyword }, headers: { Authorization: `Bearer ${token}` } });
      const users = res.data.users || [];
      if (users.length === 0) { alert('검색 결과가 존재하지 않습니다.'); return; }
      
      const newItems = users.map(u => ({
        target_type: 'user',
        target_id: String(u.target_id || u.userid || u.user_id || u.id || u.emp_no || ''),
        target_name: u.name || u.target_name || '',
        position: u.position || '',
        dept_name: u.dept_name || u.department || '',
        disp_target_name: `${u.name || u.target_name || ''}${u.position ? ' / ' + u.position : ''}${u.dept_name || u.department ? ' (' + (u.dept_name || u.department) + ')' : ''}`.trim()
      })).filter(item => item.target_id && item.target_id !== 'undefined');

      if (newItems.length === 0) {
        alert('유효한 사용자를 찾을 수 없습니다.');
        return;
      }

      // ── 검색 즉시 알림 및 사전 검증 ──
      const myId = String(user?.id || '');
      const myUserId = String(user?.userid || '');
      const isSelfItem = (u) => {
        const uId = String(u.target_id || u.userid || u.user_id || u.id || '');
        return Boolean((myId && uId === myId) || (myUserId && uId === myUserId));
      };

      if (newItems.length === 1) {
        const item = newItems[0];
        if (isSelfItem(item)) {
          alert(`요청자 본인은 ${target === 'assignees' ? '담당자' : '참조자'}로 지정할 수 없습니다.`);
          if (target === 'assignees') setAssigneeSearchTerm(''); else setCcSearchTerm('');
          return;
        }

        if (target === 'assignees') {
          const ccIds = ccs.map(c => String(c.target_id));
          if (ccIds.includes(String(item.target_id))) {
            alert(`이미 참조자로 등록된 사용자(${item.target_name || item.name})는 담당자로 지정할 수 없습니다.`);
            setAssigneeSearchTerm('');
            return;
          }
          const assigneeIds = assignees.map(a => String(a.target_id));
          if (assigneeIds.includes(String(item.target_id))) {
            alert('이미 담당자로 등록된 사용자입니다.');
            setAssigneeSearchTerm('');
            return;
          }
        } else {
          const assigneeIds = assignees.map(a => String(a.target_id));
          if (assigneeIds.includes(String(item.target_id))) {
            alert(`이미 담당자로 등록된 사용자(${item.target_name || item.name})는 참조자로 지정할 수 없습니다.`);
            setCcSearchTerm('');
            return;
          }
          const ccIds = ccs.map(c => String(c.target_id));
          if (ccIds.includes(String(item.target_id))) {
            alert('이미 참조자로 등록된 사용자입니다.');
            setCcSearchTerm('');
            return;
          }
        }

        applySelectedUsers(newItems, target);
      } else {
        // 다건 검색 결과 중 전체가 비활성화 대상인 경우 즉시 알림
        const disabledIds = target === 'assignees'
          ? [myId, myUserId, ...ccs.map(c => String(c.target_id)), ...assignees.map(a => String(a.target_id))].filter(Boolean)
          : [myId, myUserId, ...assignees.map(a => String(a.target_id)), ...ccs.map(c => String(c.target_id))].filter(Boolean);

        const allDisabled = newItems.every(item => disabledIds.some(d => d === String(item.target_id)));
        if (allDisabled) {
          alert(`검색된 모든 사용자가 이미 ${target === 'assignees' ? '등록되었거나 요청자 본인' : '등록되었거나 요청자 본인'}이어서 지정할 수 없습니다.`);
          return;
        }

        setSearchModalUsers(newItems);
        setSearchModalTarget(target);
        setShowSearchModal(true);
      }
    } catch (err) {
      console.error('검색 실패:', err);
    }
  };

  // ── 조직도 콜백 (공통모듈 OrgSelect 미수정, 수신 데이터 정합성 검증) ──
  const handleOrgConfirm = useCallback((selected) => {
    const myId = String(user?.id || '');
    const myUserId = String(user?.userid || '');
    const isSelf = (item) => {
      const tId = String(item.target_id || item.userid || item.user_id || item.id || '');
      return Boolean((myId && tId === myId) || (myUserId && tId === myUserId));
    };

    if (orgTarget === 'assignees') {
      const formatted = selected.map(s => ({
        target_type: 'user',
        target_id: String(s.target_id || s.userid || s.user_id || s.id || ''),
        target_name: s.target_name || s.name || '',
        position: s.position || '',
        dept_name: s.dept_name || s.department || '',
        disp_target_name: s.disp_target_name || `${s.target_name || s.name || ''}${s.position ? ` / ${s.position}` : ''}${s.dept_name || s.department ? ` (${s.dept_name || s.department})` : ''}`
      })).filter(item => item.target_id && item.target_id !== 'undefined');

      // 중복 제거 (target_id 기준)
      const uniqueFormatted = [];
      const seenIds = new Set();
      formatted.forEach(item => {
        if (!seenIds.has(String(item.target_id))) {
          seenIds.add(String(item.target_id));
          uniqueFormatted.push(item);
        }
      });

      const ccIds = ccs.map(c => String(c.target_id));
      const excludedNames = [];

      const validList = uniqueFormatted.filter(item => {
        if (isSelf(item)) {
          excludedNames.push(`${item.target_name || '본인'}(요청자 본인)`);
          return false;
        }
        if (ccIds.includes(String(item.target_id))) {
          excludedNames.push(`${item.target_name}(참조자 중복)`);
          return false;
        }
        return true;
      });

      if (excludedNames.length > 0) {
        alert(`다음 사용자는 담당자로 지정할 수 없어 제외되었습니다:\n• ${excludedNames.join(', ')}`);
      }

      setAssignees(validList);
    } else if (orgTarget === 'ccs') {
      const formatted = selected.map(s => ({
        target_type: 'user',
        target_id: String(s.target_id || s.userid || s.user_id || s.id || ''),
        target_name: s.target_name || s.name || '',
        position: s.position || '',
        dept_name: s.dept_name || s.department || '',
        disp_target_name: s.disp_target_name || `${s.target_name || s.name || ''}${s.position ? ` / ${s.position}` : ''}${s.dept_name || s.department ? ` (${s.dept_name || s.department})` : ''}`
      })).filter(item => item.target_id && item.target_id !== 'undefined');

      // 중복 제거 (target_id 기준)
      const uniqueFormatted = [];
      const seenIds = new Set();
      formatted.forEach(item => {
        if (!seenIds.has(String(item.target_id))) {
          seenIds.add(String(item.target_id));
          uniqueFormatted.push(item);
        }
      });

      const assigneeIds = assignees.map(a => String(a.target_id));
      const excludedNames = [];

      const validList = uniqueFormatted.filter(item => {
        if (isSelf(item)) {
          excludedNames.push(`${item.target_name || '본인'}(요청자 본인)`);
          return false;
        }
        if (assigneeIds.includes(String(item.target_id))) {
          excludedNames.push(`${item.target_name}(담당자 중복)`);
          return false;
        }
        return true;
      });

      if (excludedNames.length > 0) {
        alert(`다음 사용자는 참조자로 지정할 수 없어 제외되었습니다:\n• ${excludedNames.join(', ')}`);
      }

      setCcs(validList);
    }
    setShowOrgPopup(false);
  }, [orgTarget, user, assignees, ccs]);

  // ── 날짜 포맷 ──
  const formatDate = (d) => {
    if (!d) return '';
    const dt = d instanceof Date ? d : new Date(d);
    if (isNaN(dt.getTime())) return '';
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
  };

  // ── 저장 / 등록 유효성 검사 ──
  const handleSubmit = async (submitType) => {
    if (!title.trim()) return alert('업무 제목을 입력해 주세요.');
    if (submitType === 'request' && assignees.length === 0) {
      return alert('담당자를 1명 이상 지정해 주세요.');
    }

    // ── 최종 안전 방어벽: 요청자 본인 포함 및 담당자-참조자 중복 검증 ──
    const myId = String(user?.id || '');
    const myUserId = String(user?.userid || '');
    const isSelfId = (id) => Boolean((myId && String(id) === myId) || (myUserId && String(id) === myUserId));

    const selfInAssignees = assignees.some(a => isSelfId(a.target_id));
    if (selfInAssignees) {
      return alert('요청자 본인은 담당자로 지정할 수 없습니다.');
    }
    const selfInCcs = ccs.some(c => isSelfId(c.target_id));
    if (selfInCcs) {
      return alert('요청자 본인은 참조자로 지정할 수 없습니다.');
    }

    const assigneeIdSet = new Set(assignees.map(a => String(a.target_id)));
    const duplicateInCc = ccs.find(c => assigneeIdSet.has(String(c.target_id)));
    if (duplicateInCc) {
      return alert(`동일한 사용자(${duplicateInCc.target_name || '사용자'})가 담당자와 참조자에 동시에 지정될 수 없습니다.`);
    }

    if (!startDate || !dueDate) {
      return alert('시작일과 마감일을 지정해 주세요.');
    }
    if (new Date(dueDate).getTime() < new Date(startDate).getTime()) {
      return alert('마감일이 시작일보다 빠를 수 없습니다.');
    }
    const contentText = editor ? editor.getText().trim() : '';
    if (submitType === 'request' && !contentText) {
      return alert('요청 내용을 입력해 주세요.');
    }

    const formData = new FormData();
    formData.append('title', title.trim());
    formData.append('priority', priority);
    formData.append('status', submitType);
    formData.append('completion_type', completionType);
    formData.append('content', editor ? editor.getHTML() : '');
    formData.append('start_date', formatDate(startDate));
    formData.append('due_date', formatDate(dueDate));

    const assigneeList = assignees.map(a => a.target_id);
    const ccList = ccs.map(c => c.target_id);
    formData.append('assignees', JSON.stringify(assigneeList));
    formData.append('ccs', JSON.stringify(ccList));
    formData.append('assigneeIds', JSON.stringify(assigneeList));
    formData.append('ccIds', JSON.stringify(ccList));

    files.filter(f => f.isNew && !f.isDeleted).forEach(f => {
      if (f.file) formData.append('files', f.file);
    });

    const deletedFileIds = files
      .filter(f => !f.isNew && f.isDeleted && f.id)
      .map(f => f.id);
    formData.append('deletedFileIds', JSON.stringify(deletedFileIds));

    try {
      if (isEdit && workOrderId) {
        await workOrderApi.update(workOrderId, formData);
        alert('업무협조 건이 수정되었습니다.');
      } else {
        await workOrderApi.create(formData);
        if (submitType === 'temp') {
          alert('임시저장되었습니다.');
        } else {
          alert('업무협조 요청이 등록되었습니다.');
        }
      }
      onSaved && onSaved();
      onClose();
    } catch (err) {
      alert('저장 실패: ' + (err.response?.data?.message || err.message));
    }
  };

  // ── [업무 관리 ▴] 액션 실행 핸들러 (WorkOrderDetailModal과 100% 동일) ──
  const handleActionClick = async (action) => {
    setShowManagementMenu(false);
    if (action === 'cancel') {
      if (!window.confirm('이 업무협조 건을 취소하시겠습니까?')) return;
    } else if (action === 'force_done') {
      if (!window.confirm('이 업무협조 건을 강제 완료 처리하시겠습니까?')) return;
    } else if (action === 'stop') {
      if (!window.confirm('이 업무협조 건을 일시 중단하시겠습니까?')) return;
    } else if (action === 'resume') {
      if (!window.confirm('중단된 업무를 재개하시겠습니까?')) return;
    }
    try {
      const res = await workOrderApi.changeAction(workOrderId, { action });
      if (res.success) {
        alert(res.message || '요청이 성공적으로 처리되었습니다.');
        onSaved && onSaved();
        onClose();
      } else {
        alert(res.message || '처리에 실패했습니다.');
      }
    } catch (err) {
      console.error('[handleActionClick] error:', err);
      alert(err.response?.data?.message || err.message || '처리 중 오류가 발생했습니다.');
    }
  };

  const handleDeleteWorkOrder = async () => {
    setShowManagementMenu(false);
    if (!window.confirm('이 업무협조 건을 삭제(삭제 보관함으로 이동)하시겠습니까?')) return;
    try {
      const res = await workOrderApi.delete(workOrderId);
      if (res.success) {
        alert(res.message || '업무협조 건이 삭제되었습니다.');
        onDeleted ? onDeleted() : (onDelete && onDelete());
        onClose();
      } else {
        alert(res.message || '삭제에 실패했습니다.');
      }
    } catch (err) {
      console.error('[handleDeleteWorkOrder] error:', err);
      alert(err.response?.data?.message || err.message || '삭제 중 오류가 발생했습니다.');
    }
  };

  const handleRestoreWorkOrder = async () => {
    if (!window.confirm('이 업무를 다시 진행 상태로 재등록(복구)하시겠습니까?')) return;
    try {
      const res = await workOrderApi.restore(workOrderId);
      if (res.success) {
        alert(res.message || '업무가 정상적으로 재등록되었습니다.');
        onSaved && onSaved();
        onClose();
      } else {
        alert(res.message || '재등록에 실패했습니다.');
      }
    } catch (err) {
      console.error('[handleRestoreWorkOrder] error:', err);
      alert(err.response?.data?.message || err.message || '재등록 중 오류가 발생했습니다.');
    }
  };

  const handleHardDeleteWorkOrder = async () => {
    if (!window.confirm('이 업무협조 건을 완전히 삭제하시겠습니까?\n삭제된 데이터는 영구적으로 복구할 수 없습니다.')) return;
    try {
      const res = await workOrderApi.hardDelete(workOrderId);
      if (res.success) {
        alert('영구 삭제되었습니다.');
        onDeleted ? onDeleted() : (onDelete && onDelete());
        onClose();
      } else {
        alert(res.message || '영구 삭제에 실패했습니다.');
      }
    } catch (err) {
      console.error('[handleHardDeleteWorkOrder] error:', err);
      alert(err.response?.data?.message || err.message || '영구 삭제 중 오류가 발생했습니다.');
    }
  };

  const handleAcceptWorkOrder = async () => {
    if (!window.confirm('이 업무를 접수하고 진행을 시작하시겠습니까?')) return;
    try {
      const res = await workOrderApi.changeAction(workOrderId, { action: 'accept' });
      if (res.success) {
        alert('업무가 성공적으로 접수되어 진행중 상태로 변경되었습니다.');
        onSaved && onSaved();
        onClose();
      } else {
        alert(res.message || '접수 처리에 실패했습니다.');
      }
    } catch (err) {
      console.error('[handleAcceptWorkOrder] error:', err);
      alert('업무 접수 중 오류가 발생했습니다.');
    }
  };

  // ── 지시자 정보 ──
  const getAuthorDisplay = () => {
    if (detailData?.author) {
      const a = detailData.author;
      const pos = a.org?.position || a.position || '';
      const dept = a.org?.dept_name || a.dept_name || '';
      return `${a.name || ''}${pos ? ' ' + pos : ''}${dept ? ' (' + dept + ')' : ''}`;
    }
    if (initialData?.author) {
      const a = initialData.author;
      const pos = a.org?.position || a.position || '';
      const dept = a.org?.dept_name || a.dept_name || '';
      return `${a.name || ''}${pos ? ' ' + pos : ''}${dept ? ' (' + dept + ')' : ''}`;
    }
    if (initialData?.author_name) {
      const pos = initialData.author_position || '';
      const dept = initialData.author_dept || '';
      return `${initialData.author_name}${pos ? ' ' + pos : ''}${dept ? ' (' + dept + ')' : ''}`;
    }
    if (detailData?.author_name) {
      const pos = detailData.author_position || '';
      const dept = detailData.author_dept || '';
      return `${detailData.author_name}${pos ? ' ' + pos : ''}${dept ? ' (' + dept + ')' : ''}`;
    }
    return user ? `${user.name || ''}${user.position ? ' ' + user.position : ''}${user.dept_name ? ' (' + user.dept_name + ')' : ''}` : '-';
  };

  const getTitle = () => {
    if (isView) return '업무협조 상세';
    if (isEdit) return '요청수정';
    return '업무협조 요청';
  };

  if (!open) return null;

  const modalContent = (
    <div
      className="wo-modal-overlay"
      style={{
        ...(zIndex ? { zIndex } : {}),
        ...(isSubModal ? { backgroundColor: 'rgba(15, 23, 42, 0.25)' } : {})
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="wo-modal-dialog">
        <div className="wo-modal-content">

          {/* ── 헤더 ── */}
          <div className="wo-modal-header" style={{ background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)', padding: '16px 24px' }}>
            <h5 className="m-0 fw-bold fs-5 text-white d-flex align-items-center gap-2">
              <i className={`fas ${isView ? 'fa-eye' : 'fa-edit'}`} />
              <span>{getTitle()}</span>
            </h5>
            <div className="d-flex align-items-center gap-3 flex-shrink-0 ms-3">
              {isView && (
                <>
                  <div
                    className="d-flex align-items-center gap-3"
                    style={{
                      fontSize: '0.8rem',
                      color: 'rgba(255,255,255,0.88)',
                      paddingRight: '14px',
                      borderRight: '1px solid rgba(255,255,255,0.25)',
                      marginTop: '1px'
                    }}
                  >
                    <span>
                      <i className="fas fa-user-edit me-1 text-info" />
                      요청자: <strong>{getAuthorDisplay()}</strong>
                    </span>
                    <span>
                      <i className="fas fa-calendar-alt me-1 text-warning" />
                      요청일: <strong>{(() => {
                        const d = detailData?.created_at || initialData?.created_at;
                        if (!d) return '-';
                        try {
                          const dateObj = new Date(d);
                          if (isNaN(dateObj.getTime())) return String(d).substring(0, 10);
                          const yyyy = dateObj.getFullYear();
                          const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
                          const dd = String(dateObj.getDate()).padStart(2, '0');
                          return `${yyyy}-${mm}-${dd}`;
                        } catch { return '-'; }
                      })()}</strong>
                    </span>
                  </div>

                  {(() => {
                    const data = detailData || initialData;
                    if (!data) return null;
                    const meta = getStatusMeta(data.status || 'request');
                    const isDelayed = (() => {
                      const today = new Date(); today.setHours(0, 0, 0, 0);
                      const due = data.due_date ? new Date(data.due_date) : null;
                      if (due) due.setHours(0, 0, 0, 0);
                      return due && (due - today) < 0;
                    })();

                    let badgeLabel = meta.label;
                    let badgeBg = '#2563eb';
                    let badgeIcon = 'fa-play-circle';

                    if (data.status === 'temp' || data.status === 'draft') {
                      badgeLabel = '임시저장'; badgeBg = '#64748b'; badgeIcon = 'fa-save';
                    } else if (data.status === 'done' || data.status === 'completed') {
                      badgeLabel = '완료'; badgeBg = '#10b981'; badgeIcon = 'fa-check-circle';
                    } else if (data.status === 'stop') {
                      badgeLabel = '중단'; badgeBg = '#c2410c'; badgeIcon = 'fa-pause-circle';
                    } else if (data.status === 'cancel' || data.status === 'canceled') {
                      badgeLabel = '취소됨'; badgeBg = '#ea580c'; badgeIcon = 'fa-ban';
                    } else if (data.status === 'review') {
                      badgeLabel = '결과검토'; badgeBg = '#8b5cf6'; badgeIcon = 'fa-file-signature';
                    } else if (isDelayed) {
                      const assignees = data.assignees || [];
                      const hasAccepted = assignees.some(a => a.status !== 'pending');
                      badgeLabel = hasAccepted ? '진행지연' : '접수지연';
                      badgeBg = '#ef4444';
                      badgeIcon = 'fa-exclamation-circle';
                    } else if (data.status === 'progress') {
                      badgeLabel = '진행중'; badgeBg = '#2563eb'; badgeIcon = 'fa-play-circle';
                    } else {
                      badgeLabel = '대기'; badgeBg = '#f59e0b'; badgeIcon = 'fa-clock';
                    }

                    return (
                      <span
                        className="badge d-inline-flex align-items-center gap-1.5 shadow-sm fw-bold"
                        style={{
                          backgroundColor: badgeBg,
                          color: '#ffffff',
                          fontSize: '0.78rem',
                          padding: '4px 10px',
                          borderRadius: '6px'
                        }}
                      >
                        <i className={`fas ${badgeIcon}`} />
                        {badgeLabel}
                      </span>
                    );
                  })()}
                </>
              )}
              <button type="button" className="btn-close btn-close-white ms-1" onClick={onClose} style={{ boxShadow: 'none' }} />
            </div>
          </div>

          {/* ── 본문 ── */}
          <div className="wo-modal-body">
            {loading ? (
              <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                <div className="spinner-border text-primary" />
              </div>
            ) : (
              <div className="row g-3">

                {/* 1. 요청 제목 */}
                <div className="col-12">
                  <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                    <label className="form-label fw-bold text-secondary mb-2" style={{ fontSize: '0.85rem' }}>
                      <i className="fas fa-file-alt me-1 text-primary" /> 요청 제목 {!isView && <span className="text-danger">*</span>}
                    </label>
                    {isView ? (
                      <div className="text-dark px-3 py-2 rounded" style={{ fontSize: '0.825rem', fontWeight: 500, backgroundColor: '#f1f5f9' }}>
                        {title || '-'}
                      </div>
                    ) : (
                      <input
                        type="text"
                        className="form-control"
                        value={title}
                        onChange={e => setTitle(e.target.value)}
                        placeholder="예: 2026년 4분기 정보시스템 정기 점검 협조 요청"
                        maxLength={150}
                        style={{ fontSize: '0.88rem' }}
                      />
                    )}
                  </div>
                </div>

                {/* 2. 중요도 & 진행상황 / 완료 처리 방식 (50:50 분할) */}
                <div className="col-12">
                  <div className="py-2 px-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                    <div className="row g-2 align-items-center">
                      {/* 좌측: 중요도 */}
                      <div className="col-12 col-md-6 border-end-md d-flex align-items-center gap-3">
                        <label className="form-label fw-bold text-secondary mb-0 flex-shrink-0" style={{ fontSize: '0.85rem' }}>
                          <i className="fas fa-flag me-1 text-primary" /> 중요도
                        </label>
                        <div className="d-flex gap-2 flex-wrap">
                          {PRIORITY_OPTIONS.map(opt => (
                            <button
                              key={opt.value}
                              type="button"
                              className={`wo-priority-btn ${priority === opt.value ? 'active ' + opt.value : ''}`}
                              onClick={!isView ? () => setPriority(opt.value) : undefined}
                              style={{ cursor: isView ? 'default' : 'pointer' }}
                            >
                              <span className="wo-priority-icon">{opt.icon}</span> {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* 우측: 진행상황 */}
                      <div className="col-12 col-md-6 ps-md-3 d-flex align-items-center gap-3">
                        <label className="form-label fw-bold text-secondary mb-0 flex-shrink-0" style={{ fontSize: '0.85rem' }}>
                          <i className="fas fa-tasks me-1 text-primary" /> 진행상황
                        </label>
                        <div className="d-flex align-items-center gap-2">
                          {(() => {
                            const currentStatus = (isView || isEdit) ? (detailData?.status || 'request') : 'request';
                            const meta = getStatusMeta(currentStatus);
                            const getDesc = (k) => {
                              switch (k) {
                                case 'cancel': return '요청자에 의해 취소된 협조 업무입니다.';
                                case 'deleted': return '삭제 보관함으로 이동된 업무입니다.';
                                case 'stop': return '요청자에 의해 일시 중단된 협조 업무입니다.';
                                case 'done': return '모든 업무 및 처리결과가 완료되었습니다.';
                                case 'review': return '처리결과가 제출되어 요청자 검토 대기 중입니다.';
                                case 'progress': return '담당자가 업무를 접수하여 정상 진행 중입니다.';
                                case 'request': return '담당자가 아직 접수하지 않은 대기 상태입니다.';
                                case 'delay_request': return '접수 마감 기한을 초과하여 접수가 지연된 상태입니다.';
                                case 'delay_progress': return '업무 완료 마감 기한을 초과하여 진행이 지연된 상태입니다.';
                                case 'delay_mixed': return '일부 담당자의 접수 및 진행이 지연된 상태입니다.';
                                default: return meta.label;
                              }
                            };
                            return (
                              <span
                                title={getDesc(meta.key)}
                                className={`wo-status-badge status-${meta.key}`}
                                style={{
                                  padding: '1.5px 9.5px',
                                  fontSize: '0.75rem',
                                  fontWeight: 700,
                                  borderRadius: '14px',
                                  lineHeight: 1.5,
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'help'
                                }}
                              >
                                {meta.label} {!isView && !isEdit && '(신규 등록)'}
                              </span>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 3. 담당자 (SafetyTrainingModal 표준 UI) */}
                <div className="col-12">
                  <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <label className="form-label fw-bold text-secondary mb-0" style={{ fontSize: '0.85rem' }}>
                        <i className="fas fa-users me-1 text-primary" /> 담당자 {!isView && <span className="text-danger">*</span>}
                        {assignees.length > 0 && <span className="ms-2 fw-semibold" style={{ fontSize: '0.75rem', color: '#64748b' }}>(총 {assignees.length}명)</span>}
                      </label>
                    </div>
                    <div className="card shadow-sm border-0 bg-light bg-opacity-50 mb-0">
                      <div className="card-body p-2">
                        {isView ? (
                          <div className="d-flex flex-wrap gap-2 align-items-center bg-white p-2 border rounded" style={{ minHeight: '48px', maxHeight: '130px', overflowY: 'auto' }}>
                            {assignees.length > 0 ? assignees.map(a => {
                              const sMeta = getAssigneeStatusMeta(a.status);
                              const pRate = a.progress_rate !== undefined && a.progress_rate !== null ? a.progress_rate : 0;
                              return (
                                <span
                                  key={a.target_id}
                                  className="badge d-inline-flex align-items-center"
                                  style={{
                                    background: '#eff6ff',
                                    color: '#1d4ed8',
                                    border: '1px solid #bfdbfe',
                                    fontWeight: 600,
                                    fontSize: '13px',
                                    padding: '4px 6px 4px 10px',
                                    gap: '6px'
                                  }}
                                >
                                  <span>{a.disp_target_name}</span>
                                  <span
                                    title={sMeta.tooltip}
                                    style={{
                                      backgroundColor: sMeta.bg,
                                      color: sMeta.color,
                                      fontSize: '11px',
                                      fontWeight: 700,
                                      padding: '1px 6px',
                                      borderRadius: '8px',
                                      cursor: 'help',
                                      lineHeight: 1.4,
                                      boxShadow: '0 1px 2px rgba(0,0,0,0.12)'
                                    }}
                                  >
                                    {sMeta.label}
                                  </span>
                                  <span
                                    className="fw-bold"
                                    style={{
                                      fontSize: '11px',
                                      color: pRate === 100 ? '#10b981' : '#2563eb',
                                      backgroundColor: '#fff',
                                      border: '1px solid rgba(0,0,0,0.12)',
                                      padding: '1px 5px',
                                      borderRadius: '4px',
                                      lineHeight: 1.4
                                    }}
                                  >
                                    {pRate}%
                                  </span>
                                </span>
                              );
                            }) : <span className="text-muted" style={{ fontSize: '13px' }}>-</span>}
                          </div>
                        ) : (
                          <>
                            <div className="form-schedule-search-bar mb-2 d-flex align-items-center w-100">
                              <div className="d-flex align-items-center gap-2">
                                <input
                                  type="text"
                                  autoComplete="off"
                                  value={assigneeSearchTerm}
                                  onChange={(e) => setAssigneeSearchTerm(e.target.value)}
                                  onKeyDown={(e) => { if (e.key === 'Enter') { if (e.nativeEvent.isComposing) return; e.preventDefault(); handleUserSearch(assigneeSearchTerm, 'assignees'); } }}
                                  placeholder="이름 검색"
                                  className="form-control d-inline-block w-auto"
                                  style={{ minWidth: '200px', fontSize: '13px', height: '32px' }}
                                />
                                <button type="button" className="btn btn-sm" style={{ backgroundColor: '#343a40', color: '#fff', border: 'none', padding: '4px 12px', borderRadius: '4px', whiteSpace: 'nowrap' }} onClick={() => handleUserSearch(assigneeSearchTerm, 'assignees')}>검색</button>
                                <button type="button" className="btn btn-sm" style={{ backgroundColor: 'transparent', border: '1px solid #007bff', color: '#007bff', padding: '4px 12px', borderRadius: '4px', whiteSpace: 'nowrap' }} onClick={() => { setOrgTarget('assignees'); setShowOrgPopup(true); }}>조직도 선택</button>
                              </div>
                              {assignees.length > 0 && (
                                <button type="button" className="btn btn-sm text-nowrap ms-auto" style={{ backgroundColor: '#fff', border: '1px solid #dc3545', color: '#dc3545', padding: '4px 12px', borderRadius: '4px', whiteSpace: 'nowrap' }} onClick={() => setAssignees([])}>
                                  <i className="fas fa-trash-alt me-1" />전체삭제
                                </button>
                              )}
                            </div>

                            {assignees.length > 0 ? (
                              <div className="d-flex flex-wrap gap-2 border rounded p-3 bg-white" style={{ minHeight: '48px', maxHeight: '130px', overflowY: 'auto' }}>
                                {assignees.map(a => {
                                  const sMeta = getAssigneeStatusMeta(a.status);
                                  const pRate = a.progress_rate !== undefined && a.progress_rate !== null ? a.progress_rate : 0;
                                  return (
                                    <span
                                      key={a.target_id}
                                      className="badge d-inline-flex align-items-center gap-2"
                                      style={{
                                        background: '#eff6ff',
                                        color: '#1d4ed8',
                                        border: '1px solid #bfdbfe',
                                        fontWeight: 600,
                                        fontSize: '13px',
                                        padding: '5px 10px'
                                      }}
                                    >
                                      <span>{a.disp_target_name}</span>
                                      {isEdit && (
                                        <>
                                          <span
                                            title={sMeta.tooltip}
                                            style={{
                                              backgroundColor: sMeta.bg,
                                              color: sMeta.color,
                                              fontSize: '11px',
                                              fontWeight: 700,
                                              padding: '1px 6px',
                                              borderRadius: '8px',
                                              cursor: 'help',
                                              lineHeight: 1.4
                                            }}
                                          >
                                            {sMeta.label}
                                          </span>
                                          <span
                                            className="fw-bold"
                                            style={{
                                              fontSize: '11px',
                                              color: pRate === 100 ? '#10b981' : '#2563eb',
                                              backgroundColor: '#fff',
                                              border: '1px solid rgba(0,0,0,0.12)',
                                              padding: '1px 5px',
                                              borderRadius: '4px',
                                              lineHeight: 1.4
                                            }}
                                          >
                                            {pRate}%
                                          </span>
                                        </>
                                      )}
                                      <i
                                        className="fas fa-times-circle text-primary"
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => setAssignees(prev => prev.filter(x => x.target_id !== a.target_id))}
                                      />
                                    </span>
                                  );
                                })}
                              </div>
                            ) : (
                              <div className="text-center py-4 border rounded bg-white text-muted" style={{ fontSize: '0.8rem' }}>
                                지정된 담당자가 없습니다. 이름 검색 또는 조직도 버튼을 눌러 추가해 주세요.
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {/* 완료 처리 방식 */}
                    {assignees.length >= 2 && (
                      <div className="px-1 d-flex align-items-center gap-4" style={{ fontSize: '0.85rem', marginTop: '11px' }}>
                        <span className="text-muted fw-bold">완료 처리 방식 :</span>
                        {isView ? (
                          <span className="p-1 px-2 bg-light rounded text-dark" style={{ display: 'inline-block' }}>
                            {completionType === 'joint' ? '1인 완료 시 전체 완료' : '각자 개별 처리 후 완료'}
                          </span>
                        ) : (
                          [{ value: 'individual', label: '각자 개별 처리 후 완료' }, { value: 'joint', label: '1인 완료 시 전체 완료' }].map(opt => (
                            <label key={opt.value} className="d-flex align-items-center gap-1 mb-0" style={{ cursor: 'pointer' }}>
                              <input type="radio" name="completionType" value={opt.value}
                                checked={completionType === opt.value}
                                onChange={() => setCompletionType(opt.value)} />
                              <span>{opt.label}</span>
                            </label>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* 4. 참조자 (SafetyTrainingModal 표준 UI) */}
                <div className="col-12">
                  <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <label className="form-label fw-bold text-secondary mb-0" style={{ fontSize: '0.85rem' }}>
                        <i className="fas fa-share-square me-1 text-info" /> 참조자
                        {ccs.length > 0 && <span className="ms-2 fw-semibold" style={{ fontSize: '0.75rem', color: '#64748b' }}>(총 {ccs.length}명)</span>}
                      </label>
                    </div>
                    <div className="card shadow-sm border-0 bg-light bg-opacity-50 mb-0">
                      <div className="card-body p-2">
                        {isView ? (
                          <div className="d-flex flex-wrap gap-2 align-items-center bg-white p-2 border rounded" style={{ minHeight: '48px', maxHeight: '130px', overflowY: 'auto' }}>
                            {ccs.length > 0 ? ccs.map(c => (
                              <span key={c.target_id} className="badge" style={{ background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0', fontWeight: 600, fontSize: '13px', padding: '5px 10px' }}>
                                {c.disp_target_name}
                              </span>
                            )) : <span className="text-muted" style={{ fontSize: '13px' }}>없음</span>}
                          </div>
                        ) : (
                          <>
                            <div className="form-schedule-search-bar mb-2 d-flex align-items-center w-100">
                              <div className="d-flex align-items-center gap-2">
                                <input
                                  type="text"
                                  autoComplete="off"
                                  value={ccSearchTerm}
                                  onChange={(e) => setCcSearchTerm(e.target.value)}
                                  onKeyDown={(e) => { if (e.key === 'Enter') { if (e.nativeEvent.isComposing) return; e.preventDefault(); handleUserSearch(ccSearchTerm, 'ccs'); } }}
                                  placeholder="이름 검색"
                                  className="form-control d-inline-block w-auto"
                                  style={{ minWidth: '200px', fontSize: '13px', height: '32px' }}
                                />
                                <button type="button" className="btn btn-sm" style={{ backgroundColor: '#343a40', color: '#fff', border: 'none', padding: '4px 12px', borderRadius: '4px', whiteSpace: 'nowrap' }} onClick={() => handleUserSearch(ccSearchTerm, 'ccs')}>검색</button>
                                <button type="button" className="btn btn-sm" style={{ backgroundColor: 'transparent', border: '1px solid #007bff', color: '#007bff', padding: '4px 12px', borderRadius: '4px', whiteSpace: 'nowrap' }} onClick={() => { setOrgTarget('ccs'); setShowOrgPopup(true); }}>조직도 선택</button>
                              </div>
                              {ccs.length > 0 && (
                                <button type="button" className="btn btn-sm text-nowrap ms-auto" style={{ backgroundColor: '#fff', border: '1px solid #dc3545', color: '#dc3545', padding: '4px 12px', borderRadius: '4px', whiteSpace: 'nowrap' }} onClick={() => setCcs([])}>
                                  <i className="fas fa-trash-alt me-1" />전체삭제
                                </button>
                              )}
                            </div>

                            {ccs.length > 0 ? (
                              <div className="d-flex flex-wrap gap-2 border rounded p-3 bg-white" style={{ minHeight: '48px', maxHeight: '130px', overflowY: 'auto' }}>
                                {ccs.map(c => (
                                  <span key={c.target_id} className="badge d-flex align-items-center gap-2" style={{ background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0', fontWeight: 600, fontSize: '13px', padding: '5px 10px' }}>
                                    <span>{c.disp_target_name}</span>
                                    <i className="fas fa-times-circle text-success" style={{ cursor: 'pointer' }} onClick={() => setCcs(prev => prev.filter(x => x.target_id !== c.target_id))} />
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <div className="text-center py-4 border rounded bg-white text-muted" style={{ fontSize: '0.8rem' }}>
                                지정된 참조자가 없습니다. 이름 검색 또는 조직도 버튼을 눌러 추가해 주세요.
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* 5. 기간 (시작일 ~ 마감일) & 진척도 */}
                <div className="col-12">
                  <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                    {(() => {
                      const currentAssignees = isView
                        ? (detailData?.assignees || initialData?.assignees || [])
                        : (assignees && assignees.length > 0 ? assignees : (detailData?.assignees || initialData?.assignees || []));
                      const overallProgress = currentAssignees.length > 0
                        ? Math.round(currentAssignees.reduce((acc, a) => acc + (Number(a.progress_rate) || 0), 0) / currentAssignees.length)
                        : (['done', 'completed'].includes(detailData?.status || initialData?.status) ? 100 : 0);
                      const isComplete = overallProgress === 100;

                      return (
                        <div className="row g-3">
                          {/* 좌측: 기간 (50%) */}
                          <div className="col-12 col-md-6">
                            <label className="form-label fw-bold text-secondary mb-2" style={{ fontSize: '0.85rem' }}>
                              <i className="fas fa-calendar-alt me-1 text-primary" /> 기간 {!isView && <span className="text-danger">*</span>}
                            </label>
                            {isView ? (
                              <div className="d-flex align-items-center gap-2">
                                <div className="p-2 bg-light rounded text-dark text-center" style={{ fontSize: '0.85rem', minHeight: '38px', width: '150px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <span className="text-secondary me-2">시작일 :</span>{formatDate(startDate) || '-'}
                                </div>
                                <span className="text-muted fw-bold">~</span>
                                <div className="p-2 bg-light rounded text-dark text-center" style={{ fontSize: '0.85rem', minHeight: '38px', width: '150px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <span className="text-secondary me-2">마감일 :</span>{formatDate(dueDate) || '-'}
                                </div>
                              </div>
                            ) : (
                              <div className="d-flex align-items-center gap-2" style={{ minHeight: '38px' }}>
                                <div className="wo-date-field" style={{ width: '150px', flex: '0 0 150px' }}>
                                  <CommonDatePicker value={startDate} onChange={setStartDate}
                                    placeholder="시작일 선택" showTime={false} />
                                </div>
                                <span className="text-muted fw-bold">~</span>
                                <div className="wo-date-field" style={{ width: '150px', flex: '0 0 150px' }}>
                                  <CommonDatePicker value={dueDate} onChange={setDueDate}
                                    placeholder="마감일 선택" showTime={false} />
                                </div>
                              </div>
                            )}
                          </div>

                          {/* 우측: 전체 진척도 (50%) */}
                          <div className="col-12 col-md-6">
                            <label className="form-label fw-bold text-secondary mb-2" style={{ fontSize: '0.85rem' }}>
                              <i className="fas fa-chart-line me-1 text-primary" /> 전체 진척도
                            </label>
                            <div className="d-flex align-items-center gap-3 p-2 bg-light rounded" style={{ minHeight: '38px' }}>
                              <div className="progress flex-grow-1" style={{ height: '8px', borderRadius: '4px', backgroundColor: '#e2e8f0' }}>
                                <div
                                  className={`progress-bar ${isComplete ? 'bg-success' : 'bg-primary'}`}
                                  style={{ width: `${overallProgress}%`, transition: 'width 0.3s ease' }}
                                />
                              </div>
                              <span className={`fw-bold ${isComplete ? 'text-success' : 'text-primary'}`} style={{ fontSize: '0.85rem', minWidth: '40px', textAlign: 'right' }}>
                                {overallProgress}%
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {/* 6. 지시 내용 (Safety 에디터 높이 조절 표준 UI) */}
                <div className="col-12">
                  <div className="p-3 bg-white rounded-3 shadow-sm border d-flex flex-column" style={{ borderColor: '#e2e8f0', height: editorHeight, transition: 'height 0.3s ease' }}>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <label className="form-label fw-bold text-secondary mb-0" style={{ fontSize: '0.85rem' }}>
                        <i className="fas fa-align-left me-1 text-primary" /> 요청 내용 {!isView && <span className="text-danger">*</span>}
                      </label>
                      {!isView && (
                        <div className="d-flex align-items-center gap-2">
                          <span className="text-secondary fw-semibold" style={{ fontSize: '0.8rem' }}>에디터 높이 :</span>
                          <select
                            className="form-select form-select-sm text-secondary"
                            style={{ width: '130px', fontSize: '0.8rem', cursor: 'pointer' }}
                            value={editorHeight}
                            onChange={(e) => setEditorHeight(e.target.value)}
                          >
                            <option value="400px">기본 (400px)</option>
                            <option value="600px">넓게 (600px)</option>
                            <option value="800px">아주 넓게 (800px)</option>
                          </select>
                        </div>
                      )}
                    </div>
                    <div className="flex-grow-1 bg-light rounded-2 p-1" style={{ minHeight: 0 }}>
                      {isView ? (
                        <div className="bg-white p-3 rounded shadow-sm h-100 overflow-auto ProseMirror wo-content-view"
                          dangerouslySetInnerHTML={{ __html: detailData?.content || title || '-' }} />
                      ) : (
                        <div className="border p-1 rounded bg-white h-100 d-flex flex-column">
                          {editor && <EditorToolbar editor={editor} />}
                          <div className="flex-grow-1 overflow-auto bg-white" style={{ minHeight: 0 }}>
                            <EditorContent editor={editor} className="editor-content h-100" />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* 7. 비고 (첨부파일) */}
                {(!isView || (files && files.filter(f => !f.isDeleted).length > 0)) && (
                  <div className="col-12">
                    <div className="p-3 bg-white rounded-3 shadow-sm border d-flex flex-column" style={{ borderColor: '#e2e8f0' }}>
                      <label className="form-label fw-bold text-secondary mb-2" style={{ fontSize: '0.85rem' }}>
                        <i className="fas fa-paperclip me-1 text-primary" /> 비고 (첨부파일)
                      </label>
                      <div className="flex-grow-1 overflow-auto bg-light rounded-2 p-1" style={{ minHeight: 0 }}>
                        <FileAttachment files={files} setFiles={setFiles} mode={isView ? 'view' : 'edit'} />
                      </div>
                    </div>
                  </div>
                )}

              </div>
            )}
          </div>

          {/* ── 푸터 (WorkOrderDetailModal과 100% 동일한 아웃라인 버튼 및 드롭업 구성) ── */}
          <div className="wo-modal-footer d-flex align-items-center justify-content-between w-100">
            {isView ? (
              <>
                {/* 좌측: [업무 관리 ▴] 드롭업 */}
                <div>
                  {canManage && !detailData?.deleted_at && (
                    <div className="position-relative dropup" ref={managementMenuRef}>
                      <button
                        type="button"
                        className="btn btn-sm px-3 fw-bold d-flex align-items-center shadow-sm"
                        onClick={() => setShowManagementMenu(prev => !prev)}
                        style={{
                          color: '#1d4ed8',
                          backgroundColor: '#eff6ff',
                          borderColor: '#bfdbfe',
                          fontSize: '0.75rem',
                          gap: '8px',
                          transition: 'all 0.15s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#dbeafe';
                          e.currentTarget.style.borderColor = '#93c5fd';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#eff6ff';
                          e.currentTarget.style.borderColor = '#bfdbfe';
                        }}
                      >
                        <i className="fas fa-tasks" style={{ fontSize: '0.75rem' }} />
                        <span>업무 관리</span>
                        <i className="fas fa-caret-up" style={{ fontSize: '0.75rem' }} />
                      </button>

                      {showManagementMenu && (
                        <div
                          className="position-absolute shadow-lg text-start"
                          style={{
                            bottom: '100%',
                            left: 0,
                            marginBottom: '10px',
                            width: '165px',
                            zIndex: 1055,
                            backgroundColor: '#f0f7ff',
                            border: '1px solid #bfdbfe',
                            borderRadius: '10px',
                            padding: '6px',
                            boxShadow: '0 12px 28px -4px rgba(0, 0, 0, 0.12), 0 4px 10px rgba(0, 0, 0, 0.04)'
                          }}
                          onClick={() => setShowManagementMenu(false)}
                        >
                          {/* 업무 일시 중단 */}
                          <button
                            type="button"
                            className="btn btn-sm w-100 text-start d-flex align-items-center rounded border-0"
                            style={{
                              padding: '5px 8px',
                              gap: '10px',
                              backgroundColor: 'transparent',
                              transition: 'all 0.15s ease'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e0f2fe'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            onClick={() => handleActionClick('stop')}
                          >
                            <div
                              className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                              style={{ width: '24px', height: '24px', backgroundColor: '#fef3c7' }}
                            >
                              <i className="fas fa-pause text-warning" style={{ fontSize: '0.7rem' }} />
                            </div>
                            <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#334155' }}>업무 일시 중단</span>
                          </button>

                          {/* 강제 완료 처리 */}
                          <button
                            type="button"
                            className="btn btn-sm w-100 text-start d-flex align-items-center rounded border-0"
                            style={{
                              padding: '5px 8px',
                              gap: '10px',
                              backgroundColor: 'transparent',
                              transition: 'all 0.15s ease'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e0f2fe'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            onClick={() => handleActionClick('force_done')}
                          >
                            <div
                              className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                              style={{ width: '24px', height: '24px', backgroundColor: '#dcfce7' }}
                            >
                              <i className="fas fa-check-double text-success" style={{ fontSize: '0.7rem' }} />
                            </div>
                            <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#16a34a' }}>강제 완료 처리</span>
                          </button>

                          <div style={{ height: '1px', backgroundColor: '#93c5fd', margin: '4px 6px' }} />

                          {/* 업무지시 취소 */}
                          <button
                            type="button"
                            className="btn btn-sm w-100 text-start d-flex align-items-center rounded border-0"
                            style={{
                              padding: '5px 8px',
                              gap: '10px',
                              backgroundColor: 'transparent',
                              transition: 'all 0.15s ease'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e0f2fe'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            onClick={() => handleActionClick('cancel')}
                          >
                            <div
                              className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                              style={{ width: '24px', height: '24px', backgroundColor: '#fee2e2' }}
                            >
                              <i className="fas fa-ban text-danger" style={{ fontSize: '0.7rem' }} />
                            </div>
                            <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#dc2626' }}>요청 취소</span>
                          </button>

                          {/* 업무지시 삭제 */}
                          <button
                            type="button"
                            className="btn btn-sm w-100 text-start d-flex align-items-center rounded border-0"
                            style={{
                              padding: '5px 8px',
                              gap: '10px',
                              backgroundColor: 'transparent',
                              transition: 'all 0.15s ease'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e0f2fe'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            onClick={handleDeleteWorkOrder}
                          >
                            <div
                              className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                              style={{ width: '24px', height: '24px', backgroundColor: '#fee2e2' }}
                            >
                              <i className="fas fa-trash-alt text-danger" style={{ fontSize: '0.7rem' }} />
                            </div>
                            <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#dc2626' }}>요청삭제</span>
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* ── 우측 영역: 버튼 그룹 ── */}
                <div className="d-flex align-items-center gap-2 ms-auto">
                  {/* [지시자/관리자 취소/삭제 상태일 때]: 복구(재등록) 및 완전삭제 버튼 */}
                  {canManage && (detailData?.status === 'canceled' || detailData?.status === 'cancel' || detailData?.deleted_at) && (
                    <>
                      <button
                        type="button"
                        className="btn btn-outline-info fw-bold btn-sm px-3 shadow-sm d-flex align-items-center gap-1.5"
                        onClick={handleRestoreWorkOrder}
                        title="업무를 정상 진행 상태로 재등록(복구)합니다."
                      >
                        <i className="fas fa-undo" />
                        <span>재등록</span>
                      </button>
                      {detailData?.deleted_at && (
                        <button
                          type="button"
                          className="btn btn-outline-danger fw-bold btn-sm px-3 shadow-sm d-flex align-items-center gap-1.5"
                          onClick={handleHardDeleteWorkOrder}
                          title="이 업무를 DB에서 완전히 삭제합니다."
                        >
                          <i className="fas fa-trash-alt" />
                          <span>완전 삭제</span>
                        </button>
                      )}
                    </>
                  )}

                  {/* [지시자/관리자 중단 상태일 때]: 업무 재개 */}
                  {canManage && detailData?.status === 'stop' && !detailData?.deleted_at && (
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm px-3 fw-bold shadow-sm d-flex align-items-center gap-1.5"
                      onClick={() => handleActionClick('resume')}
                    >
                      <i className="fas fa-play" />
                      <span>업무 재개</span>
                    </button>
                  )}

                  {/* [지시자/관리자 임시저장 상태일 때]: 지시 삭제 + 지시 수정 */}
                  {canManage && (detailData?.status === 'temp' || detailData?.status === 'draft') && !detailData?.deleted_at && (
                    <>
                      <button
                        type="button"
                        className="btn btn-sm px-3 fw-bold d-flex align-items-center gap-1.5 shadow-sm"
                        style={{
                          color: '#991b1b',
                          backgroundColor: '#fff',
                          borderColor: '#fca5a5',
                          borderWidth: '1px',
                          borderStyle: 'solid',
                          transition: 'all 0.15s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#fef2f2';
                          e.currentTarget.style.borderColor = '#ef4444';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#fff';
                          e.currentTarget.style.borderColor = '#fca5a5';
                        }}
                        onClick={handleDeleteWorkOrder}
                        title="이 업무협조 건을 삭제 보관함으로 이동합니다."
                      >
                        <i className="fas fa-trash-alt" />
                        <span>요청삭제</span>
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm px-3 fw-bold d-flex align-items-center gap-1.5"
                        onClick={() => onStartEdit && onStartEdit()}
                      >
                        <i className="fas fa-edit" />
                        <span>요청수정</span>
                      </button>
                    </>
                  )}

                  {/* [지시자/관리자 대기 상태(미접수/접수지연)일 때]: 밖으로 3개 노출 */}
                  {canManage && detailData?.status === 'request' && !detailData?.deleted_at && (
                    <>
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm px-3 fw-bold d-flex align-items-center gap-1.5 shadow-sm"
                        onClick={() => handleActionClick('cancel')}
                        title="이 업무협조 요청을 취소합니다."
                      >
                        <i className="fas fa-ban" />
                        <span>요청 취소</span>
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm px-3 fw-bold d-flex align-items-center gap-1.5 shadow-sm"
                        style={{
                          color: '#991b1b',
                          backgroundColor: '#fff',
                          borderColor: '#fca5a5',
                          borderWidth: '1px',
                          borderStyle: 'solid',
                          transition: 'all 0.15s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#fef2f2';
                          e.currentTarget.style.borderColor = '#ef4444';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#fff';
                          e.currentTarget.style.borderColor = '#fca5a5';
                        }}
                        onClick={handleDeleteWorkOrder}
                        title="이 업무협조 건을 삭제 보관함으로 이동합니다."
                      >
                        <i className="fas fa-trash-alt" />
                        <span>요청삭제</span>
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm px-3 fw-bold d-flex align-items-center gap-1.5"
                        onClick={() => onStartEdit && onStartEdit()}
                      >
                        <i className="fas fa-edit" />
                        <span>요청수정</span>
                      </button>
                    </>
                  )}

                  {/* [지시자/관리자 진행중 상태일 때]: 우측에 [지시 수정] */}
                  {canManage && (detailData?.status === 'progress' || detailData?.status === 'review') && !detailData?.deleted_at && (
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm px-3 fw-bold shadow-sm d-flex align-items-center gap-1.5"
                      onClick={() => onStartEdit && onStartEdit()}
                    >
                      <i className="fas fa-edit" />
                      <span>요청수정</span>
                    </button>
                  )}

                  {/* [담당자] 본인이 미접수(pending) 상태일 때 업무 접수 버튼 노출 (동료가 먼저 접수했더라도 본인 접수 가능) */}
                  {isAssignee && !isAuthor && (myAssignee?.status === 'pending' || (!myAssignee?.status && detailData?.status === 'request')) && !detailData?.deleted_at && !['done', 'cancel', 'canceled', 'temp', 'completed'].includes(detailData?.status) && (
                    <button
                      type="button"
                      className="btn btn-success btn-sm px-3 fw-bold shadow-sm d-flex align-items-center gap-1.5"
                      onClick={handleAcceptWorkOrder}
                    >
                      <i className="fas fa-rocket" />
                      <span>업무 접수</span>
                    </button>
                  )}

                  {/* 닫기 버튼 */}
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm px-3 fw-bold d-flex align-items-center gap-1.5 shadow-sm"
                    onClick={onClose}
                  >
                    <i className="fas fa-times" />
                    <span>닫기</span>
                  </button>
                </div>
              </>
            ) : isEdit ? (
              /* ── 수정 모드: [수정완료] + [수정 취소] ── */
              <div className="d-flex align-items-center gap-2 ms-auto">
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm px-3 fw-bold d-flex align-items-center gap-1.5 shadow-sm"
                  onClick={() => handleSubmit('update')}
                >
                  <i className="fas fa-save" />
                  <span>수정완료</span>
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm px-3 fw-bold d-flex align-items-center gap-1.5 shadow-sm"
                  onClick={onClose}
                >
                  <i className="fas fa-times" />
                  <span>수정 취소</span>
                </button>
              </div>
            ) : (
              /* ── 작성(신규 등록) 모드: [임시저장] + [등록] + [취소] ── */
              <div className="d-flex align-items-center gap-2 ms-auto">
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm px-3 fw-bold d-flex align-items-center gap-1.5 shadow-sm"
                  onClick={() => handleSubmit('temp')}
                >
                  <i className="fas fa-save" />
                  <span>임시저장</span>
                </button>
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm px-3 fw-bold d-flex align-items-center gap-1.5 shadow-sm"
                  onClick={() => handleSubmit('request')}
                >
                  <i className="fas fa-paper-plane" />
                  <span>협조요청</span>
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm px-3 fw-bold d-flex align-items-center gap-1.5 shadow-sm"
                  onClick={onClose}
                >
                  <i className="fas fa-times" />
                  <span>취소</span>
                </button>
              </div>
            )}
          </div>

        </div>
      </div>

      {/* ── 조직도 팝업 (Safety 표준 및 업무협조 배타성 적용) ── */}
      {showOrgPopup && (
        <OrgTreeSelect
          selectionMode="multiple"
          targetType="user"
          initialSelected={(orgTarget === 'assignees' ? assignees : ccs).map(a => ({
            target_type: 'user',
            target_id: String(a.target_id || a.userid || ''),
            target_name: a.target_name || a.name || a.disp_target_name?.split('/')[0]?.trim() || '',
            position: a.position || '',
            dept_name: a.dept_name || a.department || '',
            disp_target_name: a.disp_target_name
          }))}
          disabledUserIds={
            orgTarget === 'assignees'
              ? [String(user?.id || ''), String(user?.userid || ''), ...ccs.map(c => String(c.target_id))].filter(Boolean)
              : [String(user?.id || ''), String(user?.userid || ''), ...assignees.map(a => String(a.target_id))].filter(Boolean)
          }
          onConfirm={handleOrgConfirm}
          onCancel={() => setShowOrgPopup(false)}
        />
      )}

      {/* ── 검색 결과 다건 선택 모달 ── */}
      <UserSearchResultModal
        isOpen={showSearchModal}
        users={searchModalUsers}
        disabledUserIds={
          searchModalTarget === 'assignees'
            ? [String(user?.id || ''), String(user?.userid || ''), ...ccs.map(c => String(c.target_id)), ...assignees.map(a => String(a.target_id))].filter(Boolean)
            : [String(user?.id || ''), String(user?.userid || ''), ...assignees.map(a => String(a.target_id)), ...ccs.map(c => String(c.target_id))].filter(Boolean)
        }
        onConfirm={(selected) => {
          applySelectedUsers(selected, searchModalTarget);
          setShowSearchModal(false);
        }}
        onClose={() => setShowSearchModal(false)}
      />
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}

export default WorkOrderFormModal;
